import { trustedScriptUrl } from './trustedTypes'

export const GA_MEASUREMENT_ID = 'G-QHMNCY6WRY'
export const CONSENT_STORAGE_KEY = 'salestaxkit-consent-v1'

export type ConsentPreferences = {
  analytics: boolean
  advertising: boolean
}

declare global {
  interface Window {
    dataLayer: unknown[]
    gtag?: (...args: unknown[]) => void
  }
}

let initialized = false
let lastTrackedPageLocation = ''

function updateGoogleConsent(preferences: ConsentPreferences) {
  window.gtag?.('consent', 'update', {
    analytics_storage: preferences.analytics ? 'granted' : 'denied',
    ad_storage: preferences.advertising ? 'granted' : 'denied',
    ad_user_data: preferences.advertising ? 'granted' : 'denied',
    ad_personalization: preferences.advertising ? 'granted' : 'denied',
  })
}

export function readConsentPreferences(): ConsentPreferences | null {
  if (typeof window === 'undefined') return null
  try {
    const stored = window.localStorage.getItem(CONSENT_STORAGE_KEY)
    if (!stored) return null
    const parsed = JSON.parse(stored) as Partial<ConsentPreferences>
    if (typeof parsed.analytics !== 'boolean' || typeof parsed.advertising !== 'boolean') return null
    return { analytics: parsed.analytics, advertising: parsed.advertising }
  } catch {
    return null
  }
}

export function saveConsentPreferences(preferences: ConsentPreferences) {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(preferences))
  const justInitialized = initializeAnalytics(preferences)
  if (justInitialized) trackPageView(window.location.pathname)
}

export function initializeAnalytics(preferences = readConsentPreferences()) {
  if (typeof window === 'undefined') return false
  if (initialized) {
    if (preferences) updateGoogleConsent(preferences)
    return false
  }

  // Do not download the Google tag until the visitor explicitly enables at
  // least one optional measurement category. This keeps the default and
  // "Only necessary" experiences free of third-party analytics JavaScript.
  if (!preferences || (!preferences.analytics && !preferences.advertising)) return false

  window.dataLayer = window.dataLayer || []
  window.gtag = (...args: unknown[]) => { window.dataLayer.push(args) }
  window.gtag('consent', 'default', {
    analytics_storage: preferences.analytics ? 'granted' : 'denied',
    ad_storage: preferences.advertising ? 'granted' : 'denied',
    ad_user_data: preferences.advertising ? 'granted' : 'denied',
    ad_personalization: preferences.advertising ? 'granted' : 'denied',
  })
  const script = document.createElement('script')
  script.async = true
  script.src = trustedScriptUrl(`https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`)
  document.head.appendChild(script)
  window.gtag('js', new Date())
  window.gtag('config', GA_MEASUREMENT_ID, { send_page_view: false })
  initialized = true
  return true
}

export function trackPageView(route: string) {
  if (typeof window === 'undefined') return
  initializeAnalytics()
  const pageLocation = window.location.href
  if (!window.gtag || pageLocation === lastTrackedPageLocation) return
  lastTrackedPageLocation = pageLocation
  window.gtag('event', 'page_view', {
    page_title: document.title,
    page_location: pageLocation,
    page_path: route,
  })
}

export function trackEvent(name: string, parameters: Record<string, string | number | boolean> = {}) {
  if (typeof window === 'undefined') return
  initializeAnalytics()
  window.gtag?.('event', name, parameters)
}
